// SamplingEventsTab.js
// This is the Sampling Events Tab component, which will display the sampling events for the user and allow for the user to edit sampling events as needed.
// this will also use the dlpstate to handle the sampling events

import React, { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
// import { useGlobalState } from "../../../../context/GlobalStateContext";
import { shallow } from "zustand/shallow";
import { useAppStore } from "../../../../store/appStore";
import {
  parseSamplingEventsRoute,
  buildSamplingEventsPath,
  canonicalizeSamplingEventsLocation,
} from "../../../../services/SamplingEventRouteUtils";
// Components
import { WellUploadPage } from "../../../wellDataUploadPage/components/WellUploadsComponents";
import { CreateSamplingEventForm } from "../../../createSamplingEventForm/CreateSamplingEventForm";
import {
  ActiveSamplingEventsTable,
  CompletedSamplingEventsTable,
} from "./SamplingEventsTable";
import { SamplingEventForm } from "../../../samplingEventForm/sampling_event_form/SamplingEventForm";
import {
  NewSamplingEventButton,
  OpenSamplingEventButton,
  WellUploadsButton,
} from "../buttons/SamplingEventActions";
import { SAMPLING_EVENT_VIEWS } from "../../../../constants/samplingEventConstants";
import { deleteSamplingEvent as apiDeleteSamplingEvent } from "../../../../api/SamplingEventsApi";
import {
  startTask,
  getTaskStatus,
  downloadTaskResult,
} from "../../../../api/TasksApi";
// Styles
import styles from "./SamplingEventsTab.module.css";
// Socket Service
import {
  disconnectSamplingEventSocket,
  clearSamplingEventHandlers,
} from "../../../../services/samplingEventSocketService";

export const SamplingEventsTab = ({ dlpState }) => {
  const location = useLocation();
  const navigate = useNavigate();
  // Loading
  const { isActive } = useAppStore((s) => s.loading, shallow);
  const showLoading = useAppStore((s) => s.loadingActions.showLoading);
  const hideLoading = useAppStore((s) => s.loadingActions.hideLoading);
  // Notification
  const { showNotification } = useAppStore(
    (s) => s.notificationActions,
    shallow
  );
  // UI slice: view state
  const currentView = useAppStore(
    (s) => s.samplingEventsTabUI.currentView,
    shallow
  );
  const activeSamplingTab = useAppStore(
    (s) => s.samplingEventsTabUI.activeTab,
    shallow
  );
  const uiActions = useAppStore((s) => s.samplingEventsTabUIActions, shallow);
  // Sampling Events list
  const activeEvents = useAppStore(
    (s) => s.samplingEventsList.active.items,
    shallow
  );
  const completedEvents = useAppStore(
    (s) => s.samplingEventsList.completed.items,
    shallow
  );

  const activePagination = useAppStore(
    (s) => s.samplingEventsList.active.pagination,
    shallow
  );
  const completedPagination = useAppStore(
    (s) => s.samplingEventsList.completed.pagination,
    shallow
  );
  const selectedActiveId = useAppStore(
    (s) => s.samplingEventsList.active.selectedId,
    shallow
  );
  const selectedCompletedId = useAppStore(
    (s) => s.samplingEventsList.completed.selectedId,
    shallow
  );
  const setSelectedActiveSamplingEvent = useAppStore(
    (s) => s.samplingEventsListActions.setSelectedActiveSamplingEvent,
    shallow
  );
  const setSelectedCompletedSamplingEvent = useAppStore(
    (s) => s.samplingEventsListActions.setSelectedCompletedSamplingEvent,
    shallow
  );
  const fetchActiveSamplingEventsPage = useAppStore(
    (s) => s.samplingEventsListActions.fetchActiveSamplingEventsPage,
    shallow
  );
  const fetchCompletedSamplingEventsPage = useAppStore(
    (s) => s.samplingEventsListActions.fetchCompletedSamplingEventsPage,
    shallow
  );
  const resetSamplingEventsList = useAppStore(
    (s) => s.samplingEventsListActions.resetSamplingEventsList,
    shallow
  );
  const setActiveListPage = useAppStore(
    (s) => s.samplingEventsListActions.setActiveListPage,
    shallow
  );
  const setCompletedListPage = useAppStore(
    (s) => s.samplingEventsListActions.setCompletedListPage,
    shallow
  );
  const setActiveEventsPerPage = useAppStore(
    (s) => s.samplingEventsListActions.setActiveEventsPerPage,
    shallow
  );
  const setCompletedEventsPerPage = useAppStore(
    (s) => s.samplingEventsListActions.setCompletedEventsPerPage,
    shallow
  );

  // Sampling sites
  const fetchSamplingSites = useAppStore(
    (s) => s.samplingSitesActions.fetchSamplingSites
  );

  // Pulling all site locations and active sampling events
  useEffect(() => {
    const initializeSamplingEventState = async () => {
      showLoading();
      try {
        await fetchSamplingSites();
        await fetchActiveSamplingEventsPage();
        await fetchCompletedSamplingEventsPage();
      } catch (error) {
        console.error("Error initializing sampling event state:", error);
      } finally {
        hideLoading();
      }
    };

    initializeSamplingEventState();
    // On unmount of the SamplingEventsTab page, disconnect socket entirely.
    return () => {
      try {
        clearSamplingEventHandlers();
      } catch {}
      try {
        disconnectSamplingEventSocket();
      } catch {}
    };
  }, []);

  // Deep-link processing: canonicalize and open create/event views
  const processedRef = useRef(null);
  useEffect(() => {
    const canonical = canonicalizeSamplingEventsLocation(location);
    if (canonical) {
      navigate(canonical, { replace: true });
      return;
    }

    const { mode, eventId } = parseSamplingEventsRoute(
      location.pathname,
      location.search
    );
    const key = `se:${mode || "table"}:${eventId || ""}`;
    if (processedRef.current === key) return;
    processedRef.current = key;

    (async () => {
      try {
        if (mode === "create") {
          uiActions.showCreateFormView();
          return;
        }
        if (mode === "well_uploads") {
          uiActions.showWellUploadsView();
          return;
        }
        if (mode === "event" && eventId) {
          showLoading();
          const res = await useAppStore
            .getState()
            .activeSamplingEventActions.fetchActiveSamplingEvent(eventId);
          if (res?.ok) uiActions.showEventFormView();
          else
            showNotification({
              type: "error",
              title: "Failed to load sampling event",
              message: res.error || "Unknown error",
            });
          hideLoading();
          return;
        }
        // default to table
        uiActions.showTableView();
      } catch (e) {
        hideLoading();
      }
    })();
  }, [
    location.pathname,
    location.search,
    navigate,
    uiActions,
    showLoading,
    hideLoading,
    showNotification,
  ]);

  const handleNewSamplingEvent = () => {
    uiActions.showCreateFormView();
    navigate(buildSamplingEventsPath({ mode: "create" }));
  };

  // Handle showing the well uploads page, with navigation so we can go back
  const handleOpenWellUploads = () => {
    navigate(buildSamplingEventsPath({ mode: "well_uploads" }));
  };

  // Handle pagination change
  const handleActivePageChange = async (page) => {
    setActiveListPage(page);
    await fetchActiveSamplingEventsPage();
  };
  const handleCompletedPageChange = async (page) => {
    setCompletedListPage(page);
    await fetchCompletedSamplingEventsPage();
  };

  // Page size selectors (10,25,50) like DraftsTab
  const handleActivePageSizeChange = async (e) => {
    showLoading("Loading sampling events...");
    const newPageSize = parseInt(e.target.value, 10);
    try {
      setActiveEventsPerPage(newPageSize);
      await fetchActiveSamplingEventsPage();
    } finally {
      hideLoading();
    }
  };

  const handleCompletedPageSizeChange = async (e) => {
    showLoading("Loading sampling events...");
    const newPageSize = parseInt(e.target.value, 10);
    try {
      setCompletedEventsPerPage(newPageSize);
      await fetchCompletedSamplingEventsPage();
    } finally {
      hideLoading();
    }
  };

  // Handle row selection
  const handleActiveRowSelect = (samplingEvent) => {
    setSelectedActiveSamplingEvent(samplingEvent);
  };
  const handleCompletedRowSelect = (samplingEvent) => {
    setSelectedCompletedSamplingEvent(samplingEvent);
  };

  // Handle event deletion
  const handleDeleteEvent = async (eventId) => {
    if (!eventId) {
      showNotification({
        type: "error",
        title: "No sampling event selected",
      });
      return;
    }
    if (
      !window.confirm("Are you sure you want to delete this sampling event?")
    ) {
      return;
    }
    showLoading();
    try {
      const res = await apiDeleteSamplingEvent(eventId);
      if (res?.status === 200) {
        showNotification({
          type: "success",
          title: "Sampling event deleted successfully",
        });
        await fetchActiveSamplingEventsPage();
      } else {
        showNotification({
          type: "error",
          title: "Failed to delete sampling event",
          message: res.error || "Unknown error",
        });
      }
    } catch (error) {
      showNotification({
        type: "error",
        title: "Failed to delete sampling event",
        message: error.message || "Unknown error",
      });
    } finally {
      hideLoading();
    }
  };

  // Opening a sampling event to start working on it
  const handleOpenSamplingEvent = async (samplingEventId) => {
    if (!samplingEventId) {
      showNotification({
        type: "error",
        title: "No sampling event selected",
      });
      return;
    }

    navigate(
      buildSamplingEventsPath({ mode: "event", eventId: samplingEventId })
    );

    // Getting the sampling event data
    showLoading();
    try {
      const res = await useAppStore
        .getState()
        .activeSamplingEventActions.fetchActiveSamplingEvent(samplingEventId);
      if (!res?.ok) throw new Error("Failed to load sampling event");
      uiActions.showEventFormView();
    } catch (error) {
      console.error("Error opening sampling event:", error);
      showNotification({
        type: "error",
        title: "Error opening sampling event",
        message: error.message || "Unknown error",
      });
    } finally {
      hideLoading();
    }
  };

  // Generic download flow using tasks API
  const handleFlatReportDownload = async (samplingEventId) => {
    if (!samplingEventId) {
      showNotification({
        type: "error",
        title: "No sampling event selected",
      });
      return;
    }
    showLoading("Preparing report...");
    try {
      const { data: start } = await startTask("sampling_events.flat_report", {
        sampling_event_id: samplingEventId,
      });
      const taskId = start?.task_id;
      if (!taskId) throw new Error("Failed to start report task");

      let attempts = 0;
      let ready = false;
      let failed = false;
      let errorMessage = null;
      let lastStatus = null;
      while (!ready && attempts < 60) {
        await new Promise((r) => setTimeout(r, 2000));
        const { data: status } = await getTaskStatus(taskId);
        lastStatus = status;
        ready = !!status?.ready;
        if (ready && !status?.ok) {
          failed = true;
          errorMessage = status?.error || "Task failed";
          break;
        }
        attempts += 1;
      }
      if (!ready) throw new Error("Timed out waiting for report");
      if (failed) throw new Error(errorMessage || "Task failed");

      const res = await downloadTaskResult(taskId, {
        s3DownloadUrl: lastStatus?.s3_download_url,
        filename: lastStatus?.filename,
      });
      if (res) {
        const blob = new Blob([res.data], {
          type:
            res.headers["content-type"] ||
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const cd = res.headers["content-disposition"] || "";
        const match = /filename\*?=(?:UTF-8''|")?([^";]+)/i.exec(cd);
        const filename = match
          ? decodeURIComponent(match[1])
          : "sampling_event_report.xlsx";
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (e) {
      showNotification({
        type: "error",
        title: "Report failed",
        message: e.message || "Unknown error",
      });
    } finally {
      hideLoading();
    }
  };

  const handlePdfDownload = async (samplingEventId) => {
    if (!samplingEventId) {
      showNotification({
        type: "error",
        title: "No sampling event selected",
      });
      return;
    }
    showLoading("Preparing PDF...");
    try {
      const { data: start } = await startTask(
        "sampling_events.sampling_event_pdf",
        { sampling_event_id: samplingEventId }
      );
      const taskId = start?.task_id;
      if (!taskId) throw new Error("Failed to start PDF task");

      let attempts = 0;
      let ready = false;
      let failed = false;
      let errorMessage = null;
      let lastStatus = null;
      while (!ready && attempts < 60) {
        await new Promise((r) => setTimeout(r, 2000));
        const { data: status } = await getTaskStatus(taskId);
        lastStatus = status;
        ready = !!status?.ready;
        if (ready && !status?.ok) {
          failed = true;
          errorMessage = status?.error || "Task failed";
          break;
        }
        attempts += 1;
      }
      if (!ready) throw new Error("Timed out waiting for PDF");
      if (failed) throw new Error(errorMessage || "Task failed");

      const res = await downloadTaskResult(taskId, {
        s3DownloadUrl: lastStatus?.s3_download_url,
        filename: lastStatus?.filename,
      });
      if (res) {
        const blob = new Blob([res.data], {
          type: res.headers["content-type"] || "application/pdf",
        });
        const cd = res.headers["content-disposition"] || "";
        const match = /filename\*?=(?:UTF-8''|")?([^";]+)/i.exec(cd);
        const filename = match
          ? decodeURIComponent(match[1])
          : "sampling_event.pdf";
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (e) {
      showNotification({
        type: "error",
        title: "PDF failed",
        message: e.message || "Unknown error",
      });
    } finally {
      hideLoading();
    }
  };

  const handleRingGaugeDownload = async (samplingEventId) => {
    if (!samplingEventId) {
      showNotification({
        type: "error",
        title: "No sampling event selected",
      });
      return;
    }
    showLoading("Preparing ring gauge report...");
    try {
      const { data: start } = await startTask(
        "sampling_events.ring_gauge_excel",
        { sampling_event_id: samplingEventId }
      );
      const taskId = start?.task_id;
      if (!taskId) throw new Error("Failed to start ring gauge task");

      let attempts = 0;
      let ready = false;
      let failed = false;
      let errorMessage = null;
      let lastStatus = null;
      while (!ready && attempts < 60) {
        await new Promise((r) => setTimeout(r, 2000));
        const { data: status } = await getTaskStatus(taskId);
        lastStatus = status;
        ready = !!status?.ready;
        if (ready && !status?.ok) {
          failed = true;
          errorMessage = status?.error || "Task failed";
          break;
        }
        attempts += 1;
      }
      if (!ready) throw new Error("Timed out waiting for ring gauge report");
      if (failed) throw new Error(errorMessage || "Task failed");

      const res = await downloadTaskResult(taskId, {
        s3DownloadUrl: lastStatus?.s3_download_url,
        filename: lastStatus?.filename,
      });
      if (res) {
        const blob = new Blob([res.data], {
          type:
            res.headers["content-type"] ||
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const cd = res.headers["content-disposition"] || "";
        const match = /filename\*?=(?:UTF-8''|")?([^";]+)/i.exec(cd);
        const filename = match ? decodeURIComponent(match[1]) : "ring_gauge.xlsx";
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (e) {
      showNotification({
        type: "error",
        title: "Ring gauge report failed",
        message: e.message || "Unknown error",
      });
    } finally {
      hideLoading();
    }
  };

  // Refresh sampling events lists
  const handleRefreshSamplingEventsLists = async () => {
    showLoading("Refreshing Sampling Events...");
    await fetchActiveSamplingEventsPage();
    await fetchCompletedSamplingEventsPage();
    hideLoading();
  };

  // Function to return to the active sampling events table
  const handleBackToTable = () => {
    uiActions.showTableView();
    navigate(buildSamplingEventsPath({ mode: "table" }));
    setSelectedActiveSamplingEvent(null);
    useAppStore.getState().activeSamplingEventActions.clearActiveEvent();
  };

  // Function to handle update sampling event
  // const handleUpdateSamplingEvent = (updatedData) => {
  //   samplingEventState.setSamplingEventData(updatedData);
  // };

  // Render function based on current view
  const renderContent = () => {
    switch (currentView) {
      case SAMPLING_EVENT_VIEWS.CREATE_FORM:
        return (
          <CreateSamplingEventForm
            setShowCreateSamplingEventForm={() => uiActions.showTableView()}
            tableName={"Active Sampling Events"}
          />
        );

      case SAMPLING_EVENT_VIEWS.EVENT_FORM:
        return (
          <SamplingEventForm
            tableName={"Active Sampling Events"}
            setShowSamplingEventForm={handleBackToTable}
            samplingEventMode="edit"
            refreshListFunction={handleRefreshSamplingEventsLists}
          />
        );
      case SAMPLING_EVENT_VIEWS.WELL_UPLOADS:
        return <WellUploadPage />;

      case SAMPLING_EVENT_VIEWS.TABLE:
      default:
        return (
          <>
            <div className={styles.samplingEventsContent}>
              <div className={styles.tabButtons}>
                <button
                  className={`${styles.tabButton} ${
                    activeSamplingTab === "active" ? styles.active : ""
                  }`}
                  onClick={() => uiActions.setActiveTab("active")}
                >
                  Active
                </button>
                <button
                  className={`${styles.tabButton} ${
                    activeSamplingTab === "completed" ? styles.active : ""
                  }`}
                  onClick={() => uiActions.setActiveTab("completed")}
                >
                  Completed
                </button>
              </div>
              {activeSamplingTab === "active" ? (
                <>
                  <div className={styles.tableActions}>
                    <NewSamplingEventButton
                      newSamplingEventFunction={handleNewSamplingEvent}
                    />
                    <OpenSamplingEventButton
                      openSamplingEventFunction={() =>
                        handleOpenSamplingEvent(selectedActiveId)
                      }
                      disabled={!selectedActiveId}
                    />
                    <WellUploadsButton
                      openWellUploadsFunction={handleOpenWellUploads}
                    />
                  </div>
                  <div className={styles.headerWithRefresh}>
                    <h2>Active Sampling Events</h2>
                    <div
                      style={{
                        position: "relative",
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      <button
                        className={styles.reportButton}
                        onClick={() =>
                          handleFlatReportDownload(selectedActiveId)
                        }
                        disabled={!selectedActiveId}
                      >
                        Flat File
                      </button>
                      <button
                        className={styles.reportButton}
                        onClick={() => handlePdfDownload(selectedActiveId)}
                        disabled={!selectedActiveId}
                        style={{ marginLeft: 8 }}
                      >
                        PDF
                      </button>
                      <button
                        className={styles.reportButton}
                        onClick={() =>
                          handleRingGaugeDownload(selectedActiveId)
                        }
                        disabled={!selectedActiveId}
                        style={{ marginLeft: 8 }}
                      >
                        WL & NAPL
                      </button>
                    </div>
                  </div>
                  <div className={styles.tableWrapper}>
                    <ActiveSamplingEventsTable
                      activeSamplingEvents={activeEvents}
                      onRowSelect={handleActiveRowSelect}
                      onDoubleClick={handleOpenSamplingEvent}
                      onDelete={handleDeleteEvent}
                      selectedActiveSamplingEventId={selectedActiveId}
                      loading={false}
                      error={null}
                      pagination={activePagination}
                      onPageChange={handleActivePageChange}
                    />
                  </div>
                </>
              ) : null}
              {activeSamplingTab === "active" ? (
                <div className={styles.paginationControls}>
                  <div className={styles.paginationLeftControls}>
                    <button
                      onClick={async () => {
                        const prev = (activePagination?.currentPage || 1) - 1;
                        if (prev < 1) return;
                        await handleActivePageChange(prev);
                      }}
                      disabled={
                        (activePagination?.currentPage || 1) === 1 || isActive
                      }
                    >
                      Previous
                    </button>
                    <button
                      onClick={async () => {
                        const next = (activePagination?.currentPage || 1) + 1;
                        const total = activePagination?.totalEvents || 0;
                        const size = activePagination?.eventsPerPage || 10;
                        const maxPage = Math.ceil(total / size) || 1;
                        if (next > maxPage) return;
                        await handleActivePageChange(next);
                      }}
                      disabled={
                        (activePagination?.currentPage || 1) *
                          (activePagination?.eventsPerPage || 10) >=
                          (activePagination?.totalEvents || 0) || isActive
                      }
                    >
                      Next
                    </button>
                    <div className={styles.pageSizeSelector}>
                      <label htmlFor="activePageSize">Rows:</label>
                      <select
                        id="activePageSize"
                        className={styles.pageSizeSelect}
                        value={activePagination?.eventsPerPage || 10}
                        onChange={handleActivePageSizeChange}
                      >
                        <option value="10">10</option>
                        <option value="25">25</option>
                        <option value="50">50</option>
                      </select>
                    </div>
                  </div>
                  <span>
                    {(() => {
                      const page = activePagination?.currentPage || 1;
                      const size = activePagination?.eventsPerPage || 10;
                      const total = activePagination?.totalEvents || 0;
                      const start = Math.min((page - 1) * size + 1, total);
                      const end = Math.min(page * size, total);
                      return `Showing ${start} - ${end} of ${total} active events`;
                    })()}
                  </span>
                </div>
              ) : (
                <>
                  <div className={styles.tableActions}>
                    <NewSamplingEventButton
                      newSamplingEventFunction={handleNewSamplingEvent}
                    />
                    <OpenSamplingEventButton
                      openSamplingEventFunction={() =>
                        handleOpenSamplingEvent(selectedCompletedId)
                      }
                      disabled={!selectedCompletedId}
                    />
                    <WellUploadsButton
                      openWellUploadsFunction={handleOpenWellUploads}
                    />
                  </div>
                  <div className={styles.headerWithRefresh}>
                    <div></div>
                    <div>
                      <button
                        onClick={() =>
                          handleFlatReportDownload(selectedCompletedId)
                        }
                        disabled={!selectedCompletedId}
                      >
                        Flat File
                      </button>
                      <button
                        onClick={() => handlePdfDownload(selectedCompletedId)}
                        disabled={!selectedCompletedId}
                        style={{ marginLeft: 8 }}
                      >
                        PDF
                      </button>
                      <button
                        onClick={() =>
                          handleRingGaugeDownload(selectedCompletedId)
                        }
                        disabled={!selectedCompletedId}
                        style={{ marginLeft: 8 }}
                      >
                        Ring Gauge
                      </button>
                    </div>
                  </div>
                  <div className={styles.tableWrapper}>
                    <CompletedSamplingEventsTable
                      completedSamplingEvents={completedEvents}
                      onRowSelect={handleCompletedRowSelect}
                      onDoubleClick={handleOpenSamplingEvent}
                      selectedCompletedSamplingEventId={selectedCompletedId}
                      loading={false}
                      error={null}
                      pagination={completedPagination}
                      onPageChange={handleCompletedPageChange}
                    />
                  </div>
                </>
              )}
              {activeSamplingTab === "completed" ? (
                <div className={styles.paginationControls}>
                  <div className={styles.paginationLeftControls}>
                    <button
                      onClick={async () => {
                        const prev =
                          (completedPagination?.currentPage || 1) - 1;
                        if (prev < 1) return;
                        await handleCompletedPageChange(prev);
                      }}
                      disabled={
                        (completedPagination?.currentPage || 1) === 1 ||
                        isActive
                      }
                    >
                      Previous
                    </button>
                    <button
                      onClick={async () => {
                        const next =
                          (completedPagination?.currentPage || 1) + 1;
                        const total = completedPagination?.totalEvents || 0;
                        const size = completedPagination?.eventsPerPage || 10;
                        const maxPage = Math.ceil(total / size) || 1;
                        if (next > maxPage) return;
                        await handleCompletedPageChange(next);
                      }}
                      disabled={
                        (completedPagination?.currentPage || 1) *
                          (completedPagination?.eventsPerPage || 10) >=
                          (completedPagination?.totalEvents || 0) || isActive
                      }
                    >
                      Next
                    </button>
                    <div className={styles.pageSizeSelector}>
                      <label htmlFor="completedPageSize">Rows:</label>
                      <select
                        id="completedPageSize"
                        className={styles.pageSizeSelect}
                        value={completedPagination?.eventsPerPage || 10}
                        onChange={handleCompletedPageSizeChange}
                      >
                        <option value="10">10</option>
                        <option value="25">25</option>
                        <option value="50">50</option>
                      </select>
                    </div>
                  </div>
                  <span>
                    {(() => {
                      const page = completedPagination?.currentPage || 1;
                      const size = completedPagination?.eventsPerPage || 10;
                      const total = completedPagination?.totalEvents || 0;
                      const start = Math.min((page - 1) * size + 1, total);
                      const end = Math.min(page * size, total);
                      return `Showing ${start} - ${end} of ${total} completed events`;
                    })()}
                  </span>
                </div>
              ) : null}
            </div>
          </>
        );
    }
  };

  return (
    <div className={styles.samplingEventsTabContainer}>{renderContent()}</div>
  );
};
